// Pure formatting + grouping helpers for the analytics page (unit-tested).

import type { ActivityType } from "@ml/shared";
import type { ActionsDay } from "@ml/shared/features/analytics";
import { CATEGORICAL } from "./charts/palette";
import type { StackColumn, StackSeries } from "./charts/StackedBars";

/** Distinct actions keep separate counts and stable colours. */
export const ACTION_GROUPS: { key: string; label: string; types: ActivityType[] }[] = [
  { key: "dms", label: "DMs sent", types: ["dm_out"] },
  { key: "comments", label: "Comment replies", types: ["comment_reply", "comment_out"] },
  { key: "follows", label: "Follows", types: ["follow"] },
  { key: "likes", label: "Likes", types: ["like"] },
  { key: "posts", label: "Posts published", types: ["post_published"] },
  { key: "unfollows", label: "Unfollows", types: ["unfollow"] },
  { key: "storyViews", label: "Story views", types: ["story_view"] },
];
export const ACTION_SERIES: StackSeries[] = ACTION_GROUPS.map((g, i) => ({ key: g.key, label: g.label, color: CATEGORICAL[i]! }));

const dayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const longDayFmt = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
export function dayLabel(key: string, long = false): string {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  return (long ? longDayFmt : dayFmt).format(dt);
}

export function percent(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

export function money(cents: number): string {
  const digits = cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100);
}

export function groupActions(days: ActionsDay[]): StackColumn[] {
  return days.map((d) => {
    const values: Record<string, number> = {};
    for (const g of ACTION_GROUPS) values[g.key] = g.types.reduce((s, t) => s + (d.counts[t] ?? 0), 0);
    return { key: d.day, label: dayLabel(d.day), title: dayLabel(d.day, true), values };
  });
}

